import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/lib/markdown";
import { ProgressRing } from "@/components/ProgressRing";
import { LockedFeature } from "@/components/LockedFeature";
import { toast } from "sonner";
import { Scale, Loader2, TrendingUp, AlertTriangle, HelpCircle, GitBranch, ListChecks, Sparkles } from "lucide-react";

const PROMPTS = [
  "Should I start this business?",
  "Should I raise funding now?",
  "Should I take this partnership?",
  "Should I hire my first employee?",
];

export default function DecisionLab() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [question, setQuestion] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isPro) return (
    <LockedFeature title="Business Decision Lab" description="An AI decision engine that analyzes any business decision — weighing opportunity, risk and confidence, surfacing unknowns, and giving you a reasoned recommendation."
      perks={["Opportunity, Risk & Confidence scores", "Advantages, risks & unknowns mapped", "Multiple options with trade-offs", "Recommended next steps"]} />
  );

  const analyze = async () => {
    if (!question.trim()) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/decision-lab", { question, context });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="decision-lab-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Business Decision Lab</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Think it through with ECO.</h1>
        <p className="text-slate-400 mt-2">Describe a decision. ECO reasons through the opportunity, risks and trade-offs — and recommends next steps.</p>
      </div>

      <div className="eco-card p-6 space-y-4">
        <input data-testid="decision-question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What decision are you facing?" className="eco-input" />
        <textarea data-testid="decision-context" value={context} onChange={(e) => setContext(e.target.value)} rows={3} placeholder="Add context: numbers, situation, constraints…" className="eco-input resize-none" />
        <div className="flex flex-wrap gap-2">
          {PROMPTS.map((p) => <button key={p} onClick={() => setQuestion(p)} className="text-xs text-slate-300 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/5">{p}</button>)}
        </div>
        <button data-testid="decision-analyze-btn" onClick={analyze} disabled={loading || !question.trim()} className="btn-accent">{loading ? <Loader2 size={16} className="animate-spin" /> : <><Scale size={16} /> Analyze decision</>}</button>
      </div>

      {result && (
        <div className="space-y-5" data-testid="decision-result">
          <div className="grid grid-cols-3 gap-4">
            <ScoreCard label="Opportunity" value={result.opportunity_score} color="#10B981" />
            <ScoreCard label="Risk" value={result.risk_score} color="#EF4444" />
            <ScoreCard label="Confidence" value={result.confidence_score} color="#0055FF" />
          </div>
          <div className="eco-card p-6">
            <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-3"><Sparkles size={18} className="text-[#4d8bff]" /> ECO's reasoning</h3>
            <Markdown>{result.reasoning}</Markdown>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <ListBlock icon={TrendingUp} title="Advantages" items={result.advantages} color="#10B981" />
            <ListBlock icon={AlertTriangle} title="Risks" items={result.risks} color="#EF4444" />
            <ListBlock icon={HelpCircle} title="Unknowns" items={result.unknowns} color="#A855F7" />
          </div>
          {result.options?.length > 0 && (
            <div className="eco-card p-6">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><GitBranch size={18} className="text-[#4d8bff]" /> Options & trade-offs</h3>
              <div className="space-y-3">
                {result.options.map((o, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="font-medium text-white">{o.option}</div>
                    <div className="text-sm text-slate-400 mt-1">Trade-off: {o.tradeoff}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <ListBlock icon={ListChecks} title="Recommended next steps" items={result.recommended_actions} color="#0055FF" />
        </div>
      )}
    </div>
  );
}

function ScoreCard({ label, value, color }) {
  return (
    <div className="eco-card p-5 flex flex-col items-center text-center" data-testid={`score-${label}`}>
      <ProgressRing value={value || 0} size={72} color={color} />
      <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mt-3">{label}</div>
    </div>
  );
}
function ListBlock({ icon: I, title, items, color }) {
  if (!items?.length) return null;
  return (
    <div className="eco-card p-6">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-4"><I size={17} style={{ color }} /> {title}</h3>
      <ul className="space-y-2.5">{items.map((it, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {it}</li>)}</ul>
    </div>
  );
}
